// js/_shared/_loading-overlay.js
/* =============================================================================
   SHARED LOADING OVERLAY
   Path: /js/_shared/_loading-overlay.js
   
   Purpose: Full-screen loading overlay with spinner and optional success state.
            Used during async operations that need to block user interaction.
   
   Dependencies: 
   - /js/_shared/_utils.js (load first)
   
   Namespace: window.AppUtils.LoadingOverlay
   Global Alias: window.LoadingOverlay (for convenience)
   ========================================================================== */

window.AppUtils = window.AppUtils || {};

window.AppUtils.LoadingOverlay = (function () {
    'use strict';

    /* =========================================================================
       PRIVATE VARIABLES
       ========================================================================= */

    var _overlay = null;
    var _stylesInjected = false;
    var _isVisible = false;

    /* =========================================================================
       STYLES
       ========================================================================= */

    var _styles = [
        '.loading-overlay {',
        '    position: fixed;',
        '    inset: 0;',
        '    z-index: 10002;',
        '    display: flex;',
        '    flex-direction: column;',
        '    align-items: center;',
        '    justify-content: center;',
        '    background: rgba(255, 255, 255, 0.95);',
        '    animation: overlayFadeIn 0.2s ease-out;',
        '}',
        '.loading-overlay.is-hiding {',
        '    animation: overlayFadeOut 0.3s ease-in forwards;',
        '}',
        '.loading-overlay__spinner {',
        '    width: 48px;',
        '    height: 48px;',
        '    border: 4px solid var(--r4o-border);',
        '    border-top-color: var(--primary, #667eea);',
        '    border-radius: 50%;',
        '    animation: spin 0.8s linear infinite;',
        '}',
        '.loading-overlay__success {',
        '    width: 48px;',
        '    height: 48px;',
        '    border-radius: 50%;',
        '    background: var(--r4o-success);',
        '    display: flex;',
        '    align-items: center;',
        '    justify-content: center;',
        '    animation: successPop 0.3s ease-out;',
        '}',
        '.loading-overlay__success svg {',
        '    width: 24px;',
        '    height: 24px;',
        '    color: var(--r4o-white);',
        '}',
        '.loading-overlay__title {',
        '    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
        '    font-size: 18px;',
        '    font-weight: 600;',
        '    color: var(--r4o-text-strong);',
        '    margin-top: 16px;',
        '}',
        '.loading-overlay__subtitle {',
        '    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
        '    font-size: 14px;',
        '    color: var(--r4o-text-muted);',
        '    margin-top: 4px;',
        '}',
        '@keyframes overlayFadeIn {',
        '    from { opacity: 0; }',
        '    to { opacity: 1; }',
        '}',
        '@keyframes overlayFadeOut {',
        '    from { opacity: 1; }',
        '    to { opacity: 0; }',
        '}',
        '@keyframes spin {',
        '    from { transform: rotate(0deg); }',
        '    to { transform: rotate(360deg); }',
        '}',
        '@keyframes successPop {',
        '    0% { transform: scale(0); }',
        '    70% { transform: scale(1.1); }',
        '    100% { transform: scale(1); }',
        '}'
    ].join('\n');

    /* =========================================================================
       INITIALIZATION
       ========================================================================= */

    function _injectStyles() {
        if (_stylesInjected) return;

        var style = document.createElement('style');
        style.id = 'loading-overlay-styles';
        style.textContent = _styles;
        document.head.appendChild(style);
        _stylesInjected = true;
    }

    /* =========================================================================
       CORE FUNCTIONS
       ========================================================================= */

    /**
     * Show loading overlay
     * @param {string} [title='Loading...'] - Title text
     * @param {string} [subtitle] - Subtitle text
     */
    function show(title, subtitle) {
        if (_isVisible) {
            // Update existing overlay
            _updateContent(title, subtitle);
            return;
        }

        _injectStyles();

        _overlay = document.createElement('div');
        _overlay.id = 'loadingOverlay';
        _overlay.className = 'loading-overlay';

        _overlay.innerHTML = [
            '<div class="loading-overlay__spinner"></div>',
            '<div class="loading-overlay__title">' + _escapeHtml(title || 'Loading...') + '</div>',
            subtitle ? '<div class="loading-overlay__subtitle">' + _escapeHtml(subtitle) + '</div>' : ''
        ].join('\n');

        document.body.appendChild(_overlay);
        document.body.style.overflow = 'hidden';
        _isVisible = true;
    }

    /**
     * Update overlay content
     * @param {string} title
     * @param {string} [subtitle]
     */
    function _updateContent(title, subtitle) {
        if (!_overlay) return;

        var titleEl = _overlay.querySelector('.loading-overlay__title');
        var subtitleEl = _overlay.querySelector('.loading-overlay__subtitle');

        if (titleEl) {
            titleEl.textContent = title || 'Loading...';
        }

        if (subtitle) {
            if (subtitleEl) {
                subtitleEl.textContent = subtitle;
            } else {
                var newSubtitle = document.createElement('div');
                newSubtitle.className = 'loading-overlay__subtitle';
                newSubtitle.textContent = subtitle;
                _overlay.appendChild(newSubtitle);
            }
        } else if (subtitleEl) {
            subtitleEl.remove();
        }
    }

    /**
     * Escape HTML to prevent XSS
     * @param {string} str
     * @returns {string}
     */
    function _escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
    }

    /**
     * Hide loading overlay
     * @param {Object} [options] - Options
     * @param {boolean} [options.showSuccess=false] - Show success checkmark before hiding
     * @param {string} [options.successTitle='Done!'] - Success title
     * @param {number} [options.successDuration=800] - How long to show success state
     * @returns {Promise} Resolves when overlay is fully hidden
     */
    function hide(options) {
        options = options || {};

        return new Promise(function (resolve) {
            if (!_overlay || !_isVisible) {
                resolve();
                return;
            }

            if (options.showSuccess) {
                // Show success state first
                _showSuccess(options.successTitle || 'Done!');

                setTimeout(function () {
                    _fadeOut(resolve);
                }, options.successDuration || 800);
            } else {
                _fadeOut(resolve);
            }
        });
    }

    /**
     * Show success state
     * @param {string} title
     */
    function _showSuccess(title) {
        if (!_overlay) return;

        var spinner = _overlay.querySelector('.loading-overlay__spinner');
        var titleEl = _overlay.querySelector('.loading-overlay__title');
        var subtitleEl = _overlay.querySelector('.loading-overlay__subtitle');

        // Replace spinner with success icon
        if (spinner) {
            var successEl = document.createElement('div');
            successEl.className = 'loading-overlay__success';
            successEl.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">' +
                '<polyline points="20 6 9 17 4 12"></polyline></svg>';
            spinner.parentNode.replaceChild(successEl, spinner);
        }

        // Update title
        if (titleEl) {
            titleEl.textContent = title;
        }

        // Remove subtitle
        if (subtitleEl) {
            subtitleEl.remove();
        }
    }

    /**
     * Fade out and remove overlay
     * @param {Function} callback
     */
    function _fadeOut(callback) {
        if (!_overlay) {
            callback();
            return;
        }

        _overlay.classList.add('is-hiding');

        setTimeout(function () {
            if (_overlay && _overlay.parentNode) {
                _overlay.parentNode.removeChild(_overlay);
            }
            _overlay = null;
            _isVisible = false;
            document.body.style.overflow = '';
            callback();
        }, 300);
    }

    /**
     * Check if overlay is currently visible
     * @returns {boolean}
     */
    function isVisible() {
        return _isVisible;
    }

    /**
     * Update overlay title and subtitle without hiding
     * @param {string} title - New title
     * @param {string} [subtitle] - New subtitle
     */
    function update(title, subtitle) {
        if (!_overlay || !_isVisible) return;
        _updateContent(title, subtitle);
    }

    /**
     * Wrap an async operation with loading overlay
     * @param {Promise|Function} promiseOrFn - Promise or function returning promise
     * @param {Object} [options] - Options
     * @param {string} [options.title] - Loading title
     * @param {string} [options.subtitle] - Loading subtitle
     * @param {boolean} [options.showSuccess=true] - Show success on completion
     * @param {string} [options.successTitle] - Success title
     * @returns {Promise} Result of the wrapped operation
     */
    function wrap(promiseOrFn, options) {
        options = options || {};
        var showSuccess = options.showSuccess !== false;

        show(options.title, options.subtitle);

        var promise = typeof promiseOrFn === 'function' ? promiseOrFn() : promiseOrFn;

        return promise
            .then(function (result) {
                return hide({
                    showSuccess: showSuccess,
                    successTitle: options.successTitle
                }).then(function () {
                    return result;
                });
            })
            .catch(function (error) {
                return hide().then(function () {
                    throw error;
                });
            });
    }

    /* =========================================================================
       PUBLIC API
       ========================================================================= */

    return {
        show: show,
        hide: hide,
        update: update,
        isVisible: isVisible,
        wrap: wrap
    };

})();

/* =============================================================================
   GLOBAL ALIAS
   ========================================================================== */

window.LoadingOverlay = window.AppUtils.LoadingOverlay;